import { useCallback } from 'react';
import { matchPath, useNavigate } from 'react-router-dom';

import { clientApi } from '~/api/generated-api';
import { useAppDispatch } from '~/app/store';
import useSessionId from '~/common/hooks/useSessionId';
import { createSafeSessionId } from '~/common/utils/common.utils';
import {
  resetGeofenceList,
  setSelectedGeofence,
} from '~/store/geofence/geofenceSlice';

/** Cache tags of the records a Sidekick action can change. */
export const SIDEKICK_ACTION_RECORD_TAGS = [
  'geofence-controller',
  'driver-profile-controller',
] as const;

const ORGANIZATION_PATH = '/organizations/:organizationsId/*';

/**
 * Follows a Sidekick result link to the record its action changed. Record
 * pages render cached copies, and the geofence page shows the selected
 * geofence, or the first one in the stored list, without fetching it, so
 * all of those are dropped before navigating.
 * @returns A callback taking the in-app path to open.
 */
const useSidekickResultNavigate = (): ((path: string) => void) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const geofenceSessionId = useSessionId(true, false);

  return useCallback(
    (path: string) => {
      // The dock sits outside the routes, so its session can't fall back to
      // the organization in the URL the way the geofence page's does.
      const pathSessionId = createSafeSessionId(
        matchPath(ORGANIZATION_PATH, path)?.params.organizationsId,
        geofenceSessionId
      );
      dispatch(setSelectedGeofence(null));
      dispatch(resetGeofenceList({ sessionId: geofenceSessionId }));
      dispatch(resetGeofenceList({ sessionId: pathSessionId }));
      dispatch(clientApi.util.invalidateTags([...SIDEKICK_ACTION_RECORD_TAGS]));
      navigate(path);
    },
    [dispatch, geofenceSessionId, navigate]
  );
};

export default useSidekickResultNavigate;
